import { Avatar, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import InfoIcons from '../InfoIcons'
import ResourceDropdown from '../ResourceDropdown'
import ContextualPopover from '../popovers/ContextualPopover'
import { getResourceData } from './utils'
import { RESIDENT, SHIFT } from 'screens/Rostering/constants'
import { Tooltip } from 'antd'
import { ResidentResource, UserResource } from 'screens/Rostering/types'
import { capitalize } from 'lodash'
import { useSelector } from 'react-redux'
import { Home } from '@lmc/ts-common'
import { getNominative } from '@lmc/common/utils'

type UserResourceProps = {
  resource: UserResource | ResidentResource
  type?: 'resident' | 'user' | 'shift'
  metadata?: { unassignedHours: number }
}

const Resource = ({ resource, type, metadata }: UserResourceProps) => {
  const { t } = useTranslation()

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )

  if (!resource) {
    return null
  }

  const {
    primaryText,
    secondaryText,
    avatarUrl,
    resourceNotes,
    resourcePhone,
    resourceEmail,
    canDoOvertime,
    hoursText,
    isUserResource,
    active,
    deleted,
    residentStatus,
  } = getResourceData({ t, resource, type, residentNominative })

  const isShiftResource = type === SHIFT

  const isResidentResource = type === RESIDENT

  const getPopOverContent = () => {
    if (isShiftResource) {
      return 'ALLOCATED_REQUIRED_WEEKLY'
    }

    if (isUserResource) {
      return 'ALLOCATED_CONTRACTED_HOURS'
    }

    return 'FILLED_CARE_PACKAGE_HOURS'
  }

  const unassignedHoursText =
    metadata?.unassignedHours && `${Math.round(metadata?.unassignedHours)}h`

  return (
    <ResourceWrapper>
      {resource.id === 'unassigned' ? (
        <FlexColumn>
          <FlexRow>
            <PrimaryText level={1.5} weight="medium">
              {t('UNASSIGNED')}
            </PrimaryText>
          </FlexRow>
          <PrimaryText level={2} weight="medium">
            {unassignedHoursText}
          </PrimaryText>
        </FlexColumn>
      ) : (
        <ResourceContent>
          <FlexRow>
            <Info>
              {!isShiftResource ? (
                <>
                  <AvatarContainer>
                    <StyledAvatar url={avatarUrl} size={18} />
                  </AvatarContainer>

                  <Tooltip
                    color={theme.color.neutral.color3}
                    placement="top"
                    title={<TooltipTitle>{secondaryText}</TooltipTitle>}
                  >
                    <PrimaryText level={1.5} weight="medium">
                      {primaryText}
                    </PrimaryText>
                  </Tooltip>
                </>
              ) : (
                <PrimaryText level={1.5} weight="medium">
                  {primaryText}
                </PrimaryText>
              )}
            </Info>
            {!deleted && <ResourceDropdown resource={resource} type={type} />}
          </FlexRow>
          <FlexRow>
            <Info>
              <ContextualPopover text={t(getPopOverContent())}>
                <PrimaryText level={2} weight="medium">
                  {hoursText}
                </PrimaryText>
              </ContextualPopover>
              {isUserResource && canDoOvertime && (
                <>
                  <Text level={2} weight="medium">
                    ·
                  </Text>
                  <ContextualPopover text={t('WILLING_TO_WORK_OVERTIME')}>
                    <Text level={2} weight="medium">
                      OT
                    </Text>
                  </ContextualPopover>
                </>
              )}
            </Info>
            {!isShiftResource && (
              <InfoIcons
                email={resourceEmail}
                phone={resourcePhone}
                notes={resourceNotes}
                visit={type === 'resident'}
                inactive={!active && !isShiftResource}
                deleted={deleted}
                residentStatus={residentStatus}
              />
            )}
          </FlexRow>
          {isResidentResource && (
            <FlexRow>
              <StatusText level={2}>{capitalize(residentStatus)}</StatusText>
            </FlexRow>
          )}
        </ResourceContent>
      )}
    </ResourceWrapper>
  )
}

const ResourceWrapper = styled.div`
  padding: 8px 12px 8px 12px;
`

const AvatarContainer = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledAvatar = styled(Avatar)`
  border: 0.5px solid ${(props) => props.theme.color.neutral.color7};
`

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const ResourceContent = styled(FlexColumn)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const FlexRow = styled.div`
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 16px;
`
const Info = styled(FlexRow)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  justify-content: start;
`

const PrimaryText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color3};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
`

const TooltipTitle = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color11};
`

const StatusText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
`

export default Resource
